// Lightweight hand-written types for the ga_* schema. Curated, matches the
// migrations in the Supabase project.

export type UserRole = 'member' | 'vendor' | 'platform';
export type VendorStatus = 'draft' | 'active' | 'paused' | 'inactive';
export type OfferStatus = 'draft' | 'active' | 'archived';
export type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed';
export type PaymentStatus = 'unpaid' | 'authorized' | 'paid' | 'refunded' | 'failed';
export type PaymentMode = 'prepaid' | 'rsvp_based';
export type SeatingStyle = 'standing' | 'mixed' | 'tables' | 'section' | 'private_room';

export type AddOn = {
  key: string;
  label: string;
  price_cents_per_guest: number;
};

export const SEATING_STYLES: { value: SeatingStyle; label: string; tagline: string }[] = [
  { value: 'standing',      label: 'Standing room',       tagline: 'Open floor, lean-in vibe.' },
  { value: 'mixed',         label: 'Mixed seating',       tagline: 'Some seats, some standing.' },
  { value: 'tables',        label: 'Reserved tables',     tagline: 'Tables held for your group.' },
  { value: 'section',       label: 'Private section',     tagline: 'A roped-off corner of the bar.' },
  { value: 'private_room',  label: 'Private room',        tagline: 'Your own room, door closed.' },
];

// Curated default catalog vendors can edit. Keys are stable; labels and prices
// the vendor can override per offer. Anything in the catalog the vendor
// doesn't toggle on simply doesn't appear at checkout.
export const ADDON_PRESETS: AddOn[] = [
  { key: 'private_room',     label: 'Private room',              price_cents_per_guest: 1200 },
  { key: 'private_section',  label: 'Private section',           price_cents_per_guest: 700  },
  { key: 'reserved_tables',  label: 'Reserved tables',           price_cents_per_guest: 400  },
  { key: 'dedicated_server', label: 'Dedicated server',          price_cents_per_guest: 500  },
  { key: 'welcome_drink',    label: 'Welcome cocktail on arrival', price_cents_per_guest: 600 },
  { key: 'extra_hour',       label: 'Extra hour at the end',     price_cents_per_guest: 800  },
  { key: 'av_screen',        label: 'AV / screen for slides',    price_cents_per_guest: 300  },
  { key: 'coat_check',       label: 'Coat check',                price_cents_per_guest: 200  },
  { key: 'custom_signage',   label: 'Custom signage',            price_cents_per_guest: 200  },
  { key: 'photo_station',    label: 'Photo / Polaroid station',  price_cents_per_guest: 500  },
];
export type RsvpStatus = 'pending' | 'yes' | 'no' | 'maybe';

export type UserProfile = {
  id: string;
  email: string;
  email_domain: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  team: string | null;
  role: UserRole;
  created_at: string;
  updated_at: string;
};

export const TEAM_OPTIONS = [
  'Marketing',
  'Operations',
  'Sales',
  'Product',
  'Engineering',
  'Design',
  'HR / People',
  'Finance',
  'Legal',
  'Customer Success',
  'Executive Assistant',
  'Founder / CEO',
  'Other',
] as const;

export type Organization = {
  id: string;
  domain: string;
  name: string | null;
  created_at: string;
};

export type OrgMember = {
  org_id: string;
  user_id: string;
  role: string;
  created_at: string;
};

export type Vendor = {
  id: string;
  slug: string;
  name: string;
  neighborhood: string | null;
  address: string | null;
  vibes: string[];
  capacity: number | null;
  hero_image_url: string | null;
  description: string | null;
  status: VendorStatus;
  contact_email: string | null;
  contact_phone: string | null;
  photo_urls: string[];
  postal_code: string | null;
  place_id: string | null;
  category: string | null;
  website: string | null;
  lat: number | null;
  lng: number | null;
  created_at: string;
  updated_at: string;
};

export type VendorMember = {
  vendor_id: string;
  user_id: string;
  role: string;
  created_at: string;
};

export type OfferTier = 'starter' | 'standard' | 'premium';

export type Offer = {
  id: string;
  vendor_id: string;
  title: string;
  description: string | null;
  price_cents: number;
  headcount_min: number;
  headcount_max: number;
  window_days: string;
  window_start: string;
  window_end: string;
  status: OfferStatus;
  drinks_per_guest: number;
  appetizers_total: number;
  entrees_total: number;
  benefits: string[];
  days_of_week: string[];
  photo_urls: string[];
  tier: OfferTier | null;
  drinks_included: string[];
  food_included: boolean;
  food_appetizers_count: number;
  food_appetizers_per_guests: number;
  dietary_options: string[];
  add_ons: AddOn[];
  seating_style: SeatingStyle;
  created_at: string;
  updated_at: string;
};

export type PackageConfig = {
  tier: OfferTier;
  drinks_per_guest: number;
  drinks_included: string[];
  food_included: boolean;
  food_appetizers_count: number;
  food_appetizers_per_guests: number;
  dietary_options: string[];
  price_cents: number;
};

export type Booking = {
  id: string;
  offer_id: string;
  vendor_id: string;
  organizer_user_id: string;
  organization_id: string | null;
  booking_date: string;
  booking_time: string;
  headcount: number;
  total_cents: number;
  status: BookingStatus;
  payment_status: PaymentStatus;
  payment_mode: PaymentMode;
  payment_ref: string | null;
  notes: string | null;
  add_ons: AddOn[];
  add_ons_total_cents: number;
  cancellation_date: string | null;
  cancellation_reasons: string[];
  cancellation_other_reason: string | null;
  created_at: string;
  updated_at: string;
};

export const CANCELLATION_REASONS = [
  'Schedule conflict',
  'Changed our plans',
  'Found another spot',
  'Headcount changed',
  'Wrong date or time',
  'Weather',
  'Booked by mistake',
] as const;

export type BookingInvite = {
  id: string;
  booking_id: string;
  email: string;
  name: string | null;
  rsvp_status: RsvpStatus;
  token: string;
  created_at: string;
  responded_at: string | null;
};

export type BookingEventKind =
  | 'booking_created'
  | 'invite_added'
  | 'rsvp_yes'
  | 'rsvp_no'
  | 'rsvp_maybe'
  | 'booking_cancelled';

export type BookingEvent = {
  id: string;
  booking_id: string;
  kind: BookingEventKind;
  actor_email: string | null;
  actor_name: string | null;
  payload: Record<string, unknown>;
  created_at: string;
};

export type VendorApplication = {
  id: string;
  bar_name: string;
  contact_name: string;
  contact_email: string;
  neighborhood: string | null;
  phone: string | null;
  capacity: number | null;
  notes: string | null;
  status: string;
  created_at: string;
  applicant_user_id: string | null;
  address: string | null;
  description: string | null;
  website: string | null;
  drinks_per_guest: number | null;
  appetizers_total: number | null;
  entrees_total: number | null;
  headcount_min: number | null;
  headcount_max: number | null;
  days_of_week: string[] | null;
  start_time: string | null;
  end_time: string | null;
  benefits: string[] | null;
  price_cents: number | null;
  photo_urls: string[] | null;
  offer_title: string | null;
  offer_description: string | null;
  approved_vendor_id: string | null;
  packages: PackageConfig[];
  add_ons: AddOn[];
  seating_style: SeatingStyle;
};

export const DRINK_OPTIONS = ['Cocktails', 'Well drinks', 'Beer', 'Wine', 'Spirits', 'Mocktails', 'Other non-alcoholic'] as const;
export const DRINK_DEFAULTS = ['Cocktails', 'Well drinks', 'Beer', 'Wine', 'Other non-alcoholic'] as const;
export const DIETARY_OPTIONS = ['Vegan options', 'Vegetarian options', 'Gluten-free', 'Dairy-free', 'Nut-free', 'Halal', 'Kosher'] as const;
export const TIERS: { tier: OfferTier; name: string; tagline: string }[] = [
  { tier: 'starter',  name: 'Starter',  tagline: 'Best for casual after-work drinks' },
  { tier: 'standard', name: 'Standard', tagline: 'Most popular for teams' },
  { tier: 'premium',  name: 'Premium',  tagline: 'Celebrations & client events' },
];

export type FeaturedLocation = {
  id: string;
  slug: string;
  name: string;
  neighborhood: string | null;
  address: string | null;
  description: string | null;
  hero_image_url: string | null;
  photo_urls: string[];
  vibes: string[];
  category: string | null;
  price_label: string | null;
  offer_label: string | null;
  position: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type VendorRequest = {
  id: string;
  vendor_id: string | null;
  location_name: string;
  location_address: string | null;
  location_neighborhood: string | null;
  location_image_url: string | null;
  requester_user_id: string | null;
  requester_email: string | null;
  notes: string | null;
  status: string;
  created_at: string;
};

export const BENEFIT_OPTIONS = [
  'Private room',
  'Private section',
  'Dedicated server',
  'Custom signage',
  'AV / screen for slides',
  'Music selection',
  'Outdoor patio',
  'Welcome drink on arrival',
  'Extended hours',
  'Coat check',
] as const;

export const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export const NEIGHBORHOODS = [
  'NoMad', 'Flatiron', 'Murray Hill', 'Midtown', 'Midtown East',
  'Theater District', 'Garment District', "Hell's Kitchen",
  'Chelsea', 'West Village', 'East Village', 'SoHo', 'NoHo',
  'TriBeCa', 'FiDi', 'Lower East Side', 'Upper East Side', 'Upper West Side',
  'Harlem', 'East Harlem', 'Roosevelt Island', 'Manhattan',
] as const;
